import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import DocumentsList from "../components/DocumentsList";
import BulkUploadDialog from "../components/BulkUploadDialog";
import StatusBadge from "../components/StatusBadge";
import { fmtDate, statusFromExpiry, COMPANY_DOC_CATEGORIES, WORKER_DOC_CATEGORIES, SITE_DOC_CATEGORIES, EQUIPMENT_DOC_CATEGORIES } from "../lib/utils-app";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Building2, Plus, Users, HardHat, Wrench, X, Pencil, FileDown, Sparkles, Mail } from "lucide-react";
import { TID } from "../constants/testIds";
import { useAuth } from "../context/AuthContext";
import { canManage, canUpload } from "../lib/utils-app";
import { MissingDocsPanel } from "../components/MissingDocs";
import RegistryLookup from "../components/RegistryLookup";

export default function CompanyDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const manage = canManage(user), upload = canUpload(user);
  const [missingKey, setMissingKey] = useState(0);
  const [company, setCompany] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [sites, setSites] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [tab, setTab] = useState("info");

  const [openWorker, setOpenWorker] = useState(false);
  const [openSite, setOpenSite] = useState(false);
  const [openEquipment, setOpenEquipment] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [openBulk, setOpenBulk] = useState(false);
  const [docsKey, setDocsKey] = useState(0);
  const [editForm, setEditForm] = useState({});

  const loadAll = useCallback(async () => {
    const [c, w, s, e] = await Promise.all([
      api.get(`/companies/${id}`),
      api.get("/workers", { params: { company_id: id } }),
      api.get("/sites", { params: { company_id: id } }),
      api.get("/equipment", { params: { company_id: id } }),
    ]);
    setCompany(c.data); setWorkers(w.data); setSites(s.data); setEquipment(e.data);
    setEditForm(c.data);
  }, [id]);
  useEffect(() => { loadAll(); }, [loadAll]);

  if (!company) return <div className="text-slate-500">Caricamento…</div>;

  return (
    <div className="space-y-6">
      <div>
        <Link to="/aziende" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft size={14} /> Aziende
        </Link>
      </div>

      <header className="bg-white border border-slate-200 rounded-lg p-6 flex flex-wrap gap-4 justify-between">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-lg bg-blue-50 text-blue-800 flex items-center justify-center">
            <Building2 size={26} />
          </div>
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-blue-700 mb-1">Scheda azienda</div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{company.ragione_sociale}</h1>
            <div className="text-sm text-slate-500 font-mono mt-1">
              {company.partita_iva && `P.IVA ${company.partita_iva}`}
              {company.codice_fiscale && ` · CF ${company.codice_fiscale}`}
              {company.codice_ateco && ` · ATECO ${company.codice_ateco}`}
            </div>
            {company.studio_name && <div className="mt-1 inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-800 px-2 py-0.5 rounded-full" data-testid="company-studio-badge">Studio: {company.studio_name}</div>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {manage && <button onClick={async () => {
            try {
              const { data } = await api.get(`/companies/${company.id}/worker-summary`);
              if (!data.length) { toast.info("Nessun attestato lavoratori scaduto o in scadenza"); return; }
              if (!window.confirm(`Inviare ora il riepilogo di ${data.length} attestati a ${company.email || company.pec || "(nessuna email aziendale!)"} e ai destinatari collegati?`)) return;
              await api.post(`/admin/send-worker-summary?company_id=${company.id}`);
              toast.success("Riepilogo in invio all'azienda");
            } catch { toast.error("Errore invio riepilogo"); }
          }} data-testid="send-worker-summary"
            className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-lg text-sm">
            <Mail size={14} /> Riepilogo lavoratori
          </button>}
          {upload && <button onClick={() => setOpenBulk(true)} data-testid="bulk-upload-button"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-slate-900 text-white px-3 py-2 rounded-lg text-sm">
            <Sparkles size={14} /> Caricamento massivo AI
          </button>}
          {manage && <button onClick={async () => {
            try {
              const res = await api.get(`/companies/${company.id}/report.pdf`, { responseType: "blob" });
              const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
              const a = document.createElement("a"); a.href = url; a.download = `Report-${company.ragione_sociale}.pdf`; a.click();
              window.URL.revokeObjectURL(url);
              toast.success("Report generato");
            } catch { toast.error("Errore generazione PDF"); }
          }} data-testid="export-pdf"
            className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg text-sm">
            <FileDown size={14} /> Report PDF
          </button>}
          {manage && <button onClick={() => setOpenEdit(true)} className="inline-flex items-center gap-2 border border-slate-300 hover:bg-slate-50 px-3 py-2 rounded-lg text-sm">
            <Pencil size={14} /> Modifica
          </button>}
        </div>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="info" data-testid="tab-info">Informazioni</TabsTrigger>
          <TabsTrigger value="documenti" data-testid="tab-documenti">Documenti aziendali</TabsTrigger>
          <TabsTrigger value="lavoratori" data-testid="tab-lavoratori">Lavoratori ({workers.length})</TabsTrigger>
          <TabsTrigger value="cantieri" data-testid="tab-cantieri">Cantieri ({sites.length})</TabsTrigger>
          <TabsTrigger value="attrezzature" data-testid="tab-attrezzature">Attrezzature ({equipment.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="info">
          <div className="mb-4"><MissingDocsPanel key={missingKey} companyId={company.id} /></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              ["Sede legale", company.sede_legale],
              ["Sede operativa", company.sede_operativa],
              ["PEC", company.pec],
              ["Email", company.email],
              ["Telefono", company.telefono],
              ["Datore di lavoro / Legale rappresentante", company.datore_lavoro],
              ["Attività svolta", company.attivita],
              ["Note", company.note],
            ].map(([k, v]) => (
              <div key={k} className="bg-white border border-slate-200 rounded-lg p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-mono">{k}</div>
                <div className="mt-1 text-slate-900">{v || "—"}</div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="documenti">
          <DocumentsList key={docsKey} entityType="company" entityId={company.id} companyId={company.id} categories={COMPANY_DOC_CATEGORIES} onChanged={() => setMissingKey(k => k + 1)} />
        </TabsContent>

        <TabsContent value="lavoratori">
          {manage && <div className="flex justify-end mb-3">
            <button onClick={() => setOpenWorker(true)} data-testid={TID.addWorker}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg text-sm">
              <Plus size={16} /> Nuovo lavoratore
            </button>
          </div>}
          {workers.length === 0 ? <EmptyState icon={Users} text="Nessun lavoratore" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {workers.map(w => (
                <Link key={w.id} to={`/lavoratori/${w.id}`} className={`card-lift block bg-white border border-slate-200 rounded-lg p-4 ${w.stato === "cessato" ? "opacity-60" : ""}`}>
                  <div className="font-display font-semibold text-slate-900 flex items-center justify-between gap-2">{w.cognome} {w.nome}{w.stato === "cessato" && <span className="text-[10px] uppercase bg-slate-200 text-slate-600 rounded-full px-2 py-0.5">Cessato</span>}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">{w.codice_fiscale || "—"}</div>
                  <div className="text-sm text-slate-600 mt-2">{w.mansione || "—"}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="cantieri">
          {manage && <div className="flex justify-end mb-3">
            <button onClick={() => setOpenSite(true)} data-testid={TID.addSite}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg text-sm">
              <Plus size={16} /> Nuovo cantiere
            </button>
          </div>}
          {sites.length === 0 ? <EmptyState icon={HardHat} text="Nessun cantiere" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sites.map(s => (
                <Link key={s.id} to={`/cantieri/${s.id}`} className="card-lift block bg-white border border-slate-200 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-semibold text-slate-900">{s.denominazione}</div>
                      <div className="text-sm text-slate-600 mt-1">{s.committente}</div>
                    </div>
                    <span className={`status-pill ${s.stato === "attivo" ? "status-valid" : s.stato === "sospeso" ? "status-expiring" : "status-expired"}`}>
                      {s.stato}
                    </span>
                  </div>
                  <div className="text-xs text-slate-500 mt-2">{s.indirizzo}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="attrezzature">
          {manage && <div className="flex justify-end mb-3">
            <button onClick={() => setOpenEquipment(true)} data-testid={TID.addEquipment}
              className="inline-flex items-center gap-2 bg-blue-700 hover:bg-blue-800 text-white px-3 py-2 rounded-lg text-sm">
              <Plus size={16} /> Nuova attrezzatura
            </button>
          </div>}
          {equipment.length === 0 ? <EmptyState icon={Wrench} text="Nessuna attrezzatura" /> : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {equipment.map(e => (
                <Link key={e.id} to={`/attrezzature/${e.id}`} className="card-lift block bg-white border border-slate-200 rounded-lg p-4">
                  <div className="font-display font-semibold text-slate-900">{e.tipologia}</div>
                  <div className="text-sm text-slate-600 mt-1">{e.marca} {e.modello}</div>
                  <div className="text-xs font-mono text-slate-500 mt-1">{e.matricola && `MATR ${e.matricola}`}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {openWorker && <WorkerDialog companyId={company.id} onClose={() => setOpenWorker(false)} onCreated={loadAll} />}
      {openSite && <SiteDialog companyId={company.id} onClose={() => setOpenSite(false)} onCreated={loadAll} />}
      {openEquipment && <EquipmentDialog companyId={company.id} onClose={() => setOpenEquipment(false)} onCreated={loadAll} />}
      {openBulk && <BulkUploadDialog companyId={company.id} companyName={company.ragione_sociale} onClose={() => setOpenBulk(false)} onDone={() => { loadAll(); setDocsKey(k => k + 1); }} />}
      {openEdit && <EditCompanyDialog company={company} onClose={() => setOpenEdit(false)} onSaved={loadAll} />}
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-lg py-10 text-center text-slate-500">
      <Icon size={26} className="mx-auto mb-2 text-slate-400" />{text}
    </div>
  );
}

function DialogShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg border border-slate-200 shadow-xl w-full max-w-xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display font-semibold text-lg">{title}</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function WorkerDialog({ companyId, onClose, onCreated }) {
  const [f, setF] = useState({ company_id: companyId });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/workers", f); toast.success("Lavoratore creato"); onCreated(); onClose(); }
    catch { toast.error("Errore"); }
  };
  return (
    <DialogShell title="Nuovo lavoratore" onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[["nome", "Nome *", true], ["cognome", "Cognome *", true], ["codice_fiscale", "Codice fiscale"],
          ["data_nascita", "Data nascita", false, "date"], ["mansione", "Mansione"],
          ["data_assunzione", "Data assunzione", false, "date"], ["data_cessazione", "Data cessazione", false, "date"]].map(([k, l, req, type]) => (
          <div key={k}>
            <label className="text-sm font-medium">{l}</label>
            <input required={req} type={type || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              data-testid={`worker-${k}`}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
        ))}
        <div>
          <label className="text-sm font-medium">Stato lavoratore</label>
          <select value={f.stato || "attivo"} onChange={(e) => setF({ ...f, stato: e.target.value })} data-testid="worker-stato"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white">
            <option value="attivo">Attivo</option><option value="cessato">Cessato</option>
          </select>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" data-testid="worker-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Crea</button>
        </div>
      </form>
    </DialogShell>
  );
}

function SiteDialog({ companyId, onClose, onCreated }) {
  const [f, setF] = useState({ company_id: companyId, stato: "attivo" });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/sites", f); toast.success("Cantiere creato"); onCreated(); onClose(); }
    catch { toast.error("Errore"); }
  };
  return (
    <DialogShell title="Nuovo cantiere" onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[["denominazione", "Denominazione *", true], ["committente", "Committente"], ["indirizzo", "Indirizzo cantiere"],
          ["data_inizio", "Data inizio", false, "date"], ["data_fine_prevista", "Fine prevista", false, "date"],
          ["responsabile_lavori", "Responsabile Lavori"], ["csp", "CSP"], ["cse", "CSE"], ["altre_figure", "Altre figure"]].map(([k, l, req, type]) => (
          <div key={k} className={k === "descrizione_lavori" ? "md:col-span-2" : ""}>
            <label className="text-sm font-medium">{l}</label>
            <input required={req} type={type || "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              data-testid={`site-${k}`}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
        ))}
        <div className="md:col-span-2">
          <label className="text-sm font-medium">Descrizione lavori</label>
          <textarea value={f.descrizione_lavori || ""} onChange={(e) => setF({ ...f, descrizione_lavori: e.target.value })} rows={2}
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
        </div>
        <div>
          <label className="text-sm font-medium">Stato</label>
          <select value={f.stato} onChange={(e) => setF({ ...f, stato: e.target.value })} data-testid="site-stato"
            className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg">
            <option value="attivo">Attivo</option><option value="sospeso">Sospeso</option><option value="chiuso">Chiuso</option>
          </select>
        </div>
        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" data-testid="site-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Crea</button>
        </div>
      </form>
    </DialogShell>
  );
}

function EquipmentDialog({ companyId, onClose, onCreated }) {
  const [f, setF] = useState({ company_id: companyId });
  const submit = async (e) => {
    e.preventDefault();
    try { await api.post("/equipment", f); toast.success("Attrezzatura creata"); onCreated(); onClose(); }
    catch { toast.error("Errore"); }
  };
  return (
    <DialogShell title="Nuova attrezzatura" onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[["tipologia", "Tipologia *", true], ["marca", "Marca"], ["modello", "Modello"], ["matricola", "Matricola"],
          ["anno", "Anno"], ["numero_immatricolazione", "N° immatricolazione"]].map(([k, l, req]) => (
          <div key={k}>
            <label className="text-sm font-medium">{l}</label>
            <input required={req} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              data-testid={`equipment-${k}`}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
        ))}
        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" data-testid="equipment-submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Crea</button>
        </div>
      </form>
    </DialogShell>
  );
}

function EditCompanyDialog({ company, onClose, onSaved }) {
  const { user } = useAuth();
  const [f, setF] = useState(company);
  const [studios, setStudios] = useState([]);
  useEffect(() => { if (user?.role === "admin") api.get("/studios").then(r => setStudios(r.data)).catch(() => null); }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    try { await api.put(`/companies/${company.id}`, f); toast.success("Salvato"); onSaved(); onClose(); }
    catch { toast.error("Errore"); }
  };
  return (
    <DialogShell title="Modifica azienda" onClose={onClose}>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <RegistryLookup value={f.partita_iva || f.codice_fiscale} onFill={(fields) => setF(prev => ({ ...prev, ...fields }))} />
        {user?.role === "admin" && (
          <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
            <label className="text-sm font-medium text-blue-900">Studio di Consulenza assegnato</label>
            <select value={f.studio_id || ""} onChange={(e) => setF({ ...f, studio_id: e.target.value })} data-testid="company-studio-select"
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg bg-white">
              <option value="">— Nessuno (visibile solo allo staff) —</option>
              {studios.map(s => <option key={s.id} value={s.id}>{s.denominazione}</option>)}
            </select>
          </div>
        )}
        {["ragione_sociale", "partita_iva", "codice_fiscale", "codice_ateco", "sede_legale", "sede_operativa", "pec", "email", "telefono", "datore_lavoro", "attivita"].map(k => (
          <div key={k} className={k === "ragione_sociale" || k === "attivita" ? "md:col-span-2" : ""}>
            <label className="text-sm font-medium capitalize">{k.replace(/_/g, " ")}</label>
            <input value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg" />
          </div>
        ))}
        <div className="md:col-span-2 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-slate-600">Annulla</button>
          <button type="submit" className="px-4 py-2 bg-slate-900 text-white rounded-lg">Salva</button>
        </div>
      </form>
    </DialogShell>
  );
}
